/**
 * Pure logic behind the "source document updated → impacted sections →
 * redraft" panel: grouping the server's impact list, the default ticks, the
 * continue-sections request, the error copy, and the redraft row lifecycle
 * driven by the spine's existing signals.
 */

import type { SourceImpactHit, SourceImpactSection, SourceImpactStrength } from "../../api/client";
import type { SpineSection } from "../hooks/useVariantCRun";
import { SPINE_COMPLETED_STATUSES } from "../hooks/useVariantCRun";

export const STRENGTH_ORDER: readonly SourceImpactStrength[] = ["high", "medium", "low"];

export const STRENGTH_LABEL: Record<SourceImpactStrength, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** The chat tool that lists impacted sections (server/services/source_impact.py). */
export const FIND_IMPACTED_SECTIONS_TOOL = "find_impacted_sections";

export interface ImpactGroup {
  strength: SourceImpactStrength;
  sections: SourceImpactSection[];
}

/** Sections grouped High → Medium → Low, server order kept inside a group.
 *  Empty groups are dropped; an unknown strength reads as low. */
export function groupByStrength(sections: readonly SourceImpactSection[]): ImpactGroup[] {
  const buckets: Record<SourceImpactStrength, SourceImpactSection[]> = { high: [], medium: [], low: [] };
  for (const s of sections) {
    const key: SourceImpactStrength = s.strength === "high" || s.strength === "medium" ? s.strength : "low";
    buckets[key].push(s);
  }
  return STRENGTH_ORDER.filter((k) => buckets[k].length > 0).map((k) => ({ strength: k, sections: buckets[k] }));
}

/** High-strength sections start ticked; the writer opts the rest in. */
export function defaultTicked(sections: readonly SourceImpactSection[]): Set<string> {
  return new Set(sections.filter((s) => s.strength === "high").map((s) => s.module_id));
}

export function defaultInstruction(documentName: string): string {
  return `Source document '${documentName}' was updated to a new version; redraft using the current version.`;
}

export interface RedraftRequest {
  module_ids: string[];
  run_instructions: string;
}

/** The continue-sections body for the ticked rows: module ids in the impact
 *  list's order (deduplicated), and the trimmed instruction. Null when nothing
 *  is ticked or the instruction is blank. */
export function buildRedraftRequest(
  sections: readonly SourceImpactSection[],
  ticked: ReadonlySet<string>,
  instruction: string,
): RedraftRequest | null {
  const seen = new Set<string>();
  const moduleIds: string[] = [];
  for (const s of sections) {
    if (ticked.has(s.module_id) && !seen.has(s.module_id)) {
      seen.add(s.module_id);
      moduleIds.push(s.module_id);
    }
  }
  const text = instruction.trim();
  if (moduleIds.length === 0 || !text) return null;
  return { module_ids: moduleIds, run_instructions: text };
}

/** "§9.2 Study design" / "Study design". */
export function sectionLabel(s: Pick<SourceImpactSection, "section_number" | "title" | "module_id">): string {
  const title = s.title?.trim() || s.module_id;
  return s.section_number ? `§${s.section_number} ${title}` : title;
}

/** "p. 4" / "pp. 4, 5, 9" / "" for one hit. */
export function hitPages(hit: Pick<SourceImpactHit, "page_no" | "pages">): string {
  const pages = (hit.pages ?? []).map(String).filter(Boolean);
  if (pages.length === 0 && hit.page_no != null) pages.push(String(hit.page_no));
  if (pages.length === 0) return "";
  return pages.length === 1 ? `p. ${pages[0]}` : `pp. ${pages.join(", ")}`;
}

/** Every page any hit of a section read, deduplicated, in order. */
export function sectionPages(section: Pick<SourceImpactSection, "hits">): string[] {
  const out: string[] = [];
  for (const h of section.hits ?? []) {
    const pages = (h.pages ?? []).map(String);
    if (pages.length === 0 && h.page_no != null) pages.push(String(h.page_no));
    for (const p of pages) if (p && !out.includes(p)) out.push(p);
  }
  return out;
}

/** Writer-facing copy for a refused redraft: a run already running (409) and
 *  a section someone else holds (423, ``holderName``) get their own words. */
export function describeRedraftError(err: unknown): string {
  const e = err as { status?: number; detail?: unknown; message?: string } | null;
  const status = e?.status;
  const detail = e?.detail && typeof e.detail === "object" ? (e.detail as Record<string, unknown>) : null;
  if (status === 423) {
    const holder = typeof detail?.holderName === "string" && detail.holderName.trim() ? detail.holderName : null;
    const section = typeof detail?.sectionId === "string" && detail.sectionId ? ` (${detail.sectionId})` : "";
    return holder
      ? `${holder} has a section${section} reserved, so it can't be redrafted now. Ask them to release it, or untick it.`
      : `A section${section} is reserved by someone else, so it can't be redrafted now.`;
  }
  if (status === 409) {
    const msg = e?.message && !/^HTTP \d+$/.test(e.message) ? ` (${e.message})` : "";
    return `This run is busy${msg}. Wait for the current drafting to finish, then redraft.`;
  }
  if (err instanceof Error && err.message) return err.message;
  return "Redraft could not start.";
}

export type RedraftPhase = "redrafting" | "redrafted";

export interface RedraftRowState {
  phase: RedraftPhase;
  /** The spine showed this module being worked on again since the redraft
   *  started — so a completed status after it is the NEW draft, not the old. */
  seenActive: boolean;
}

export type RedraftStates = Readonly<Record<string, RedraftRowState>>;

/** Mark ``moduleIds`` as redrafting (fresh lifecycle). */
export function startRedraft(prev: RedraftStates, moduleIds: readonly string[]): RedraftStates {
  const next: Record<string, RedraftRowState> = { ...prev };
  for (const id of moduleIds) next[id] = { phase: "redrafting", seenActive: false };
  return next;
}

/**
 * Advance redrafting rows off the spine's existing signals (no new SSE type):
 * a row is "active" while its spine status is queued/drafting or its
 * post-draft audit is running; once it has been active and then reads
 * completed with no audit running — ``section_final`` sets ``auditPhase:
 * "done"``, and a non-audited ``section_ready`` leaves it unset — the redraft
 * landed. Returns ``prev`` itself when nothing changed.
 */
export function advanceRedraftStates(prev: RedraftStates, spine: readonly SpineSection[]): RedraftStates {
  let changed = false;
  const next: Record<string, RedraftRowState> = { ...prev };
  const byId = new Map(spine.map((s) => [s.id, s]));
  for (const [id, st] of Object.entries(prev)) {
    if (st.phase !== "redrafting") continue;
    const row = byId.get(id);
    if (!row) continue;
    const completed = SPINE_COMPLETED_STATUSES.has(row.status);
    const active = !completed || row.auditPhase === "running";
    if (active) {
      if (!st.seenActive && (row.status === "drafting" || row.status === "queued" || row.auditPhase === "running")) {
        next[id] = { ...st, seenActive: true };
        changed = true;
      }
      continue;
    }
    if (st.seenActive) {
      next[id] = { phase: "redrafted", seenActive: true };
      changed = true;
    }
  }
  return changed ? next : prev;
}

/** Module ids that turned "redrafted" between two states. */
export function newlyRedrafted(prev: RedraftStates, next: RedraftStates): string[] {
  return Object.keys(next).filter((id) => next[id].phase === "redrafted" && prev[id]?.phase !== "redrafted");
}

/** The ``document`` arg of a completed ``find_impacted_sections`` chat step,
 *  else null. Reads the ``chat_tool_step`` payload's ``toolQuery`` (the raw
 *  tool argument the stream reported). */
export function impactDocumentFromToolStep(
  payload: { kind?: string; tool?: string; status?: string; toolQuery?: string } | null | undefined,
): string | null {
  if (!payload || payload.kind !== "chat_tool_step") return null;
  if (payload.tool !== FIND_IMPACTED_SECTIONS_TOOL || payload.status !== "done") return null;
  const doc = payload.toolQuery?.trim();
  return doc ? doc : null;
}
